import React, { useState, useEffect } from 'react';
import QueryInterface from './components/QueryInterface';
import LoadingStates from './components/LoadingStates';
import ResultsDisplay from './components/ResultsDisplay';
import ErrorHandler from './components/ErrorHandler';
import { useAnalyticsAPI } from '../../../hooks/useAnalyticsAPI';
import './AIAnalyticsDashboard.css';
import ReportsHeader from '../../Common/ReportsHeader/ReportsHeader';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import queryStorageDB from '../../../js/QueryStorageDB';

const AIAnalyticsDashboard = () => {
  const [currentQuery, setCurrentQuery] = useState('');
  const [queryHistory, setQueryHistory] = useState([]);
  
  const {
    submitQuery,
    isLoading,
    loadingPhase,
    currentResults,
    error,
    clearError,
    setCurrentResults
  } = useAnalyticsAPI();

  // Load query history from IndexedDB on component mount
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const history = await queryStorageDB.getQueryHistory();
        setQueryHistory(history);
      } catch (error) {
        console.error('Failed to load query history:', error);
      }
    };
    
    loadHistory();
  }, []);

  const handleQuerySubmit = async (query) => {
    if (!query.trim()) return;
    
    setCurrentQuery(query);
    try {
      const result = await submitQuery(query);
      if (result) {
        // Save to IndexedDB with results
        await queryStorageDB.saveQuery({
          prompt: result.prompt,
          rowCount: result.rowCount,
          results: result.results,
          data: result.data,
          visualization: result.visualization
        });
        
        // Update local state
        const updatedHistory = await queryStorageDB.getQueryHistory();
        setQueryHistory(updatedHistory);
      }
    } catch (err) {
      console.error('Query submission failed:', err);
    }
  };

  const handleClearResults = () => {
    setCurrentQuery('');
    clearError();
  };

  const handleClearHistory = async () => {
    try {
      await queryStorageDB.clearAllQueries();
      setQueryHistory([]);
    } catch (error) {
      console.error('Failed to clear query history:', error);
    }
  };

  const handleDeleteQuery = async (queryId) => {
    try {
      await queryStorageDB.deleteQuery(queryId);
      const updatedHistory = await queryStorageDB.getQueryHistory();
      setQueryHistory(updatedHistory);
    } catch (error) {
      console.error('Failed to delete query:', error);
    }
  };

  const handleLoadQuery = async (queryRecord) => {
    setCurrentQuery(queryRecord.prompt);
    
    // Load the saved results if available
    if (queryRecord.results) {
      setCurrentResults({
        prompt: queryRecord.prompt,
        rowCount: queryRecord.rowCount,
        results: queryRecord.results,
        data: queryRecord.data,
        visualization: queryRecord.visualization
      });
    }
  };

  return (
    <div className="ai-analytics-dashboard">
      {/* Header */}
      <ReportsHeader/>
      {/* <header className="dashboard-header">
        <div className="header-content">
          <div className="logo-section">
            <h1 className="dashboard-title">
              Got Tea Genie
            </h1>
            <p className="dashboard-subtitle">
              Ask me anything about your business data...
            </p>
          </div>
        </div>
      </header> */}

      <div className="dashboard-layout">
        {/* Sidebar for query history */}
        <aside className={`query-sidebar`}>
          <div className="sidebar-content">
            <div className="sidebar-header">
              <h3>Recent Queries</h3>
              {queryHistory.length > 0 && (
                <button 
                  className="clear-history-btn"
                  onClick={handleClearHistory}
                  title="Clear history"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="query-history">
              {queryHistory.length === 0 ? (
                <p className="no-history">No queries yet</p>
              ) : (
                queryHistory.map((query, index) => (
                  <div 
                    key={query.id || index} 
                    className="history-item"
                  >
                    <div 
                      className="query-content"
                      onClick={() => handleLoadQuery(query)}
                    >
                      <div className="query-text">{query.prompt}</div>
                      <div className="query-meta">
                        {query.rowCount} results
                      </div>
                    </div>
                    <button 
                      className="delete-query-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteQuery(query.id);
                      }}
                      title="Delete query"
                      aria-label="Delete query"
                    >
                      ×
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </aside>

        {/* Main content area */}
        <main className="dashboard-main">
          <div className="content-container">
            {/* Query Interface */}
            <QueryInterface 
              onSubmit={handleQuerySubmit}
              isLoading={isLoading}
              currentQuery={currentQuery}
              onQueryChange={setCurrentQuery}
            />

            {/* Loading States */}
            {isLoading && (
              <LoadingStates 
                phase={loadingPhase}
                query={currentQuery}
              />
            )}

            {/* Error Display */}
            {error && (
              <ErrorHandler 
                error={error}
                onRetry={() => handleQuerySubmit(currentQuery)}
                onClear={handleClearResults}
              />
            )}

            {/* Results Display */}
            {currentResults && !isLoading && !error && (
              <ResultsDisplay 
                results={currentResults}
                onClear={handleClearResults}
              />
            )}

            {/* Welcome state when no queries */}
            {!currentResults && !isLoading && !error && !currentQuery && (
              <div className="welcome-state">
                <div className="welcome-content">
                  <h2>Welcome to Got Tea Genie</h2>
                  <p>Start by asking a question about your business data</p>
                  
                  <div className="suggestion-chips">
                    <h4>
                      <LightbulbIcon style={{ verticalAlign: 'middle', marginRight: 6 }} />
                      Try asking:
                    </h4>
                    <div className="chips-container">
                      <button 
                        className="suggestion-chip"
                        onClick={() => setCurrentQuery("Top 5 customers this month")}
                      >
                        Top 5 customers this month
                      </button>
                      <button 
                        className="suggestion-chip"
                        onClick={() => setCurrentQuery("Best selling products today")}
                      >
                        Best selling products today
                      </button>
                      <button 
                        className="suggestion-chip"
                        onClick={() => setCurrentQuery("Revenue by branch this week")}
                      >
                        Revenue by branch this week
                      </button>
                      <button 
                        className="suggestion-chip"
                        onClick={() => setCurrentQuery("Average order value trends")}
                      >
                        Average order value trends
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AIAnalyticsDashboard; 